// WebSocketContext.js

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import WebSocketService from "./WebSocketService";
import { useQueryClient } from "@tanstack/react-query";
import { getBearerToken } from "../utils/Functions";
import useUser from "../hooks/useUser";
import { SnackbarContext } from "./Snackbar";
import { snackbarSeverity } from "../hooks/useSnackbar";
import {
  APPOINTMENT_AUDIT,
  CONVO_PREVIEW_ID,
  PET_AVATAR,
  PET_BIOMETRICS_DATA,
  PETS,
  PETS_DOCUMENTS,
  PETS_GENERAL,
  PETS_NOTES,
} from "../enums/QueryKeys";
import { matchPath } from "react-router-dom";
import { PET_DETAILS_ID, SINGLE_PET_VIEW } from "../enums/UrlPaths";
import { useTranslation } from "react-i18next";
import { pendingNotificationKey } from "./NotificationsProvider";

export const WebSocketContext = createContext(null);

export function useWebSocket() {
  return useContext(WebSocketContext);
}

export function WebSocketProvider({ children }) {
  const { currentClinic, roles, profile } = useUser();
  const wsRef = useRef(null);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const processedMessageIds = useRef(new Set()); // Refs for deduplication
  const processedMessageUuid = useRef(new Set()); // Refs for deduplication


  // Refs to maintain stable references to dependencies
  const dependenciesRef = useRef({
    queryClient,
    currentClinic,
    roles,
    handleSnackbar,
    t,
  });
  useEffect(() => {
    dependenciesRef.current = {
      queryClient,
      currentClinic,
      roles,
      handleSnackbar,
      t,
    };
  });
  const handleNotifications = useCallback((message) => {
    //Create a unique identifier for the message based on all relevant fields
    const messageId = JSON.stringify({
      uuid: message.uuid,
      content: message.content,
      notificationCount: message.notificationCount,
      appointmentId: message.appointmentId,
      appointmentIds: message.appointmentIds,
      appointmentDate: message.appointmentDate,
      petId: message.petId,
      refreshHomePage: message.refreshHomePage,
      refreshPetPage: message.refreshPetPage,
      messageAdded: message.messageAdded,
      convoToUpdate: message.convoToUpdate,
      messageRead: message.messageRead,
      messagesToUpdate: message.messagesToUpdate,
    });
const uuid = message.uuid; 
    // Check if this is the same as the last message.
    // This is messy but for now it's the most stable way of getting incoming messages and not getting way too many requests 
    if (
      processedMessageIds.current.has(messageId) ||
      processedMessageUuid.current.has(uuid)
    ) {
      return;
    }

    processedMessageIds.current.add(messageId);
    processedMessageUuid.current.add(uuid);
    // Clean up old message IDs to prevent memory leaks
    if (processedMessageIds.current.size > 100) {
      const oldestId = processedMessageIds.current.values().next().value;
      processedMessageIds.current.delete(oldestId);
    }

     if (processedMessageUuid.current.size > 100) {
      const oldestId = processedMessageUuid.current.values().next().value;
      processedMessageUuid.current.delete(oldestId);
    }

    const { queryClient, currentClinic, roles, handleSnackbar, t } = dependenciesRef.current;

    if (message.notificationCount) {
      queryClient.invalidateQueries([
        pendingNotificationKey + currentClinic.id,
        { exact: true },
      ]);
    }

    if (Boolean(message?.messageAdded) === true) {
      if (Number(message?.convoToUpdate) != null) {
        queryClient.invalidateQueries(["conversations", { exact: true }]);
      }
    }

    if (message.appointmentId) {
      if (roles.user) {
        handleSnackbar(
          t("AppointmentForIsUpdated") + message.appointmentDate,
          snackbarSeverity.SUCCESS,
          true,
          false
        );
      } else {
        const locationPath = window.location.pathname;
        const match = locationPath.match(
          /^\/user\/admin\/appointements\/details\/(\d+)+$/
        );
        if (
          match !== null &&
          Number(match[1]) === Number(message.appointmentId)
        ) {
          handleSnackbar("AppointmentUpdated", snackbarSeverity.SUCCESS);
        }
      }

      queryClient.invalidateQueries([
        APPOINTMENT_AUDIT + message.appointmentId,
        { exact: true },
      ]);
    }

    if (Array.isArray(message?.appointmentIds)) {
      if (message?.appointmentIds?.length > 0) {
        message?.appointmentIds.forEach((id) => {
          queryClient.invalidateQueries([
            APPOINTMENT_AUDIT + id,
            { exact: true },
          ]);
        });
      }
    }

    if (message.refreshHomePage) {
      const refreshKey =
        roles.admin || roles.employee
          ? "CLINIC_HOMEPAGE_KEY"
          : "user_homepage_dto";
      queryClient.invalidateQueries(refreshKey, { exact: true });
    }

    if (message.petId) {
      const locationPath = window.location.pathname;
      const match =
        matchPath(PET_DETAILS_ID, locationPath) ??
        matchPath(SINGLE_PET_VIEW, locationPath);
      if (match !== null && Number(match.params.id) === Number(message.petId)) {
        handleSnackbar("PetUpdated", snackbarSeverity.SUCCESS);
      }

      // Everything the single pet view caches for this pet (general data, avatar,
      // documents list + filters, notes, biometrics); these events come from avatar/file uploads
      const petId = String(message.petId);
      const petKeys = [
        PETS_GENERAL,
        PETS_DOCUMENTS,
        PETS_NOTES,
        PET_BIOMETRICS_DATA,
        "petDocuments",
      ].map((prefix) => prefix + petId);
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          petKeys.includes(String(queryKey[0])) ||
          (queryKey[0] === PET_AVATAR && String(queryKey[1]) === petId),
      });
    }

    if (message.refreshPetPage) {
      // Every pet list key starts with "pets" (pets, pets+user+id, pets_dropdown...)
      queryClient.invalidateQueries({
        predicate: (query) => String(query.queryKey[0]).startsWith(PETS),
      });
    }
  }, []);

  const clinicId = currentClinic?.id;
  const userId = profile?.id;
  const isAdminOrEmployee = roles.admin || roles.employee;
  useEffect(() => {
    const path = isAdminOrEmployee ? "clinic" : "user";
    const id = isAdminOrEmployee ? clinicId : userId;

    if (!id) return;

    const connect = async () => {
      const token = await getBearerToken();
      const ws = new WebSocketService(token, path, id);

      ws.connect((message) => {
        handleNotifications(message);
      });

      wsRef.current = ws;
    };

    connect();

    return () => {
      wsRef.current?.disconnect();
    };
  }, [clinicId, userId, isAdminOrEmployee, handleNotifications]);
  return (
    <WebSocketContext.Provider value={wsRef.current}>
      {children}
    </WebSocketContext.Provider>
  );
}
